import { useState, useMemo } from "react";
import Loading from "components/core/blocks/Loading";
import { ProductsTable } from "components/core/blocks/ProductsTable";
import { ProductCard } from "modules/pricingv2/ProductCard";
import { SetValue, sendUpdates } from "./SetValue";
import { EnrollProductsInPricingModalForm } from "modules/pricingv2/components/modals/EnrollProductsInPricingModalForm";
import PricingPlanChoice from "./PricingPlanChoice";
import CategoryTable from "./CategoryTable";
import PricingPlanTable from "../PricingPlanTable";
import DownloadReport from "modules/perf_reports/DownloadReport";
import Accordion from "components/core/basic/Accordion";
import { useFetch } from "hooks/api";
import { keepPreviousData, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAppSelector } from "../../../redux/hooks";
import { Center } from "../../../../styled-system/jsx";
import { ButtonGroup } from "../../../components/core/basic/ButtonGroup";
import { useModal } from "@ebay/nice-modal-react";
import RemoveProductsFromPlanModal from "./RemoveProducts";

const ProductExpandedContent = ({ row }) => {
  return (
    <div style={{ backgroundColor: "#efefef" }} className="text-start">
      <ProductCard productId={row?.original?.product_id} />
    </div>
  );
};

const PricingTables = ({ marketPlace, category, filters }) => {
  const channel = useAppSelector((state) => state.channel.currentChannel);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [showSetValue, setShowSetValue] = useState(false);
  const [showPlanChoice, setShowPlanChoice] = useState(false);
  const [showEnroll, setShowEnroll] = useState(false);
  const [valueType, setValueType] = useState("min_price");
  const [productStatusFilter, setProductStatusFilter] = useState("not_enrolled");
  const removeProductsFromPlansModal = useModal(RemoveProductsFromPlanModal);

  const queryClient = useQueryClient();

  const planQuery = useFetch(["plan_report"], `/api/data_report/pricingplan_report`, filters, {
    select: (d) => d.data,
    placeholderData: keepPreviousData,
  });

  const productReportQuery = useFetch(
    ["product_report"],
    "/api/data_report/product_report",
    { ...filters, group_by: "product_id", include: "pricing", channel: channel },
    {
      select: (d) => d.data.products,
      enabled: !!category,
      placeholderData: keepPreviousData,
    },
  );

  const productsByEnrollmentStatus = useMemo(
    () => Object.groupBy(productReportQuery.data ?? [], (p) => p.pricing_plan__status),
    [productReportQuery.data],
  );
  const filteredProducts =
    (productStatusFilter === "all" ? productReportQuery.data : productsByEnrollmentStatus[productStatusFilter]) ?? [];

  const updateProducts = useMutation({
    mutationFn: async (updates) => {
      await sendUpdates("product_id", updates, marketPlace, queryClient);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [{ entity: "preview_plan_creation" }] });
      queryClient.invalidateQueries({ queryKey: ["product_report"] });
    },
  });

  const enrolledSelectedProducts =
    productReportQuery.data
      ?.filter((product) => selectedProducts.includes(product.id) && product.pricing_plan__status !== "not_enrolled")
      .map((product) => product.id) ?? [];

  const planOptions = productReportQuery.data?.reduce(
    (agg, p) => {
      if (!selectedProducts.includes(p.id)) return agg;

      agg.uniqueASINs.add(p.asin);

      if (agg.minPrice === null || 0 < p.min_price < agg.minPrice) {
        agg.minPrice = p.min_price;
      }
      if (agg.maxPrice === null || p.max_price > agg.maxPrice > 0) {
        agg.maxPrice = p.max_price;
      }

      return agg;
    },
    {
      minPrice: null,
      maxPrice: null,
      uniqueASINs: new Set(),
    },
  );

  const disablePlanAction = enrolledSelectedProducts?.length !== 0 || selectedProducts?.length === 0;

  const productById = useMemo(() => {
    const ids = {};
    if (productReportQuery.data) {
      for (const product of productReportQuery.data) {
        ids[product.id] = product;
      }
    }
    return ids;
  }, [productReportQuery.data]);

  return (
    <>
      {planQuery.isLoading && <Loading height={300} fullPage={false} />}
      {category && planQuery.data?.plan_id?.length > 0 && (
        <Accordion title={"Pricing Plans"} initialExpanded={true}>
          <PricingPlanTable pricingPlans={planQuery.data?.plan_id} />

          <DownloadReport
            url_path={"pricingplan_report"}
            filters={{
              ...filters,
              empty: true,
              kw_report: false,
            }}
            report_dl={"pricingplan"}
          />
        </Accordion>
      )}
      {category && planQuery.data?.plan_id && (
        <Accordion title={`Products`} initialExpanded={planQuery.data?.plan_id?.length === 0}>
          <ButtonGroup
            value={productStatusFilter}
            onSelect={setProductStatusFilter}
            buttons={[
              {
                label: "All",
                value: "all",
                counter: productReportQuery.data?.length,
              },
              {
                label: "Not Enrolled",
                value: "not_enrolled",
                counter: productsByEnrollmentStatus["not_enrolled"]?.length ?? 0,
              },
              {
                label: "Enrolled",
                value: "enabled",
                counter: productsByEnrollmentStatus["enabled"]?.length ?? 0,
              },
              {
                label: "Paused",
                value: "disabled",
                counter: productsByEnrollmentStatus["disabled"]?.length ?? 0,
              },
            ]}
          />
          {productReportQuery.isSuccess ?
            filteredProducts.length > 0 ?
              <ProductsTable
                selectedProducts={selectedProducts}
                onBulkChange={async (changes, clearChanges) => {
                  const updates = {};
                  Object.entries(changes).forEach(([id, changes]) => {
                    updates[id] = Object.entries(changes)
                      .filter(([key]) => ["max_price", "min_price", "shipping_cost", "landed_cost"].includes(key))
                      .map(([key, value]) => [key, parseFloat(value)]);
                  });
                  updateProducts.mutate(updates, {
                    onSuccess: () => {
                      clearChanges?.();
                    },
                  });
                }}
                marketPlace={marketPlace}
                productData={filteredProducts}
                ExpandedContent={ProductExpandedContent}
                isSaving={updateProducts.isPending}
                onSelection={(v) => {
                  setSelectedProducts(v);
                }}
                cellContext={`pricing`}
                bulkActionOptions={[
                  {
                    label: "Create Pricing Plan",
                    action: () => {
                      setShowEnroll(true);
                    },
                    disabled: disablePlanAction,
                  },
                  {
                    label: "Add to existing Pricing Plan",
                    action: () => {
                      setShowPlanChoice(true);
                    },
                    disabled: disablePlanAction || !planQuery.data || planQuery.data?.plan_id?.length === 0,
                  },
                  {
                    label: "Remove from Pricing Plans",
                    action: (selected) => {
                      removeProductsFromPlansModal.show({
                        selectedProducts: selected,
                        productById,
                        onRemove: () => setSelectedProducts([]),
                      });
                    },
                    disabled: !new Set(selectedProducts).isSubsetOf(new Set(enrolledSelectedProducts)),
                  },
                  {
                    divider: true,
                  },
                  ...[
                    "Fixed Price",
                    "Clear Fixed Price",
                    "Minimum Price",
                    "Maximum Price",
                    "Shipping Cost",
                    "Landed Cost",
                  ].map((label) => ({
                    label: label === "Clear Fixed Price" ? label : `Set ${label}`,
                    action: () => {
                      setValueType(label);
                      setShowSetValue(true);
                    },
                  })),
                ]}
              />
            : <Center fontSize={"medium"}>No products match current filter</Center>
          : <Loading />}
        </Accordion>
      )}
      {!category && planQuery.data && !planQuery.isLoading && (
        <Accordion title={"Categories"} initialExpanded={!category}>
          <CategoryTable stats={planQuery.data?.category_id} />

          <DownloadReport url_path={"category_report"} filters={filters} report_dl={"category"} />
        </Accordion>
      )}
      {showSetValue && (
        <SetValue
          showSetValue={showSetValue}
          setShowSetValue={setShowSetValue}
          valueType={valueType}
          selectedProducts={selectedProducts}
          productById={productById}
          marketPlace={marketPlace}
          setSelectedProducts={setSelectedProducts}
        />
      )}
      {showEnroll && (
        <EnrollProductsInPricingModalForm
          setShowEnroll={setShowEnroll}
          showEnroll={showEnroll}
          selectedProducts={selectedProducts}
          initialMaxPrice={planOptions.maxPrice}
          initialMinPrice={planOptions.minPrice}
          isMultiAsin={planOptions.uniqueASINs.size > 1}
          marketPlace={marketPlace}
          category={category}
          productById={productById}
        />
      )}
      {showPlanChoice && (
        <PricingPlanChoice
          plans={planQuery.data?.plan_id}
          setIsOpen={setShowPlanChoice}
          isOpen={showPlanChoice}
          productById={productById}
          selectedProducts={selectedProducts}
          setSelectedProducts={setSelectedProducts}
        />
      )}
    </>
  );
};

export default PricingTables;
